import React, { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { CATEGORIES } from '@r1-frontend/shared/constants/analytics';

import { FiltersForTariffs } from '~/src/components/FiltersForTariffs';
import { ITariff } from '~/src/entities/formattedTariff';

import {
    ETariffTypes,
    getInitialFilterItems,
} from './filterConstants';

const sendGTMevent = (actionText: string, label?: string) => {
    dataLayerPush({
        event: 'UAevent',
        category: CATEGORIES.tariffCards,
        action: actionText,
        label: label || '',
    });
};

interface IMenuItem {
    text: string,
    action: () => void,
}

export interface ITariffsFiltersProps {
    isVillage: boolean,
    children:  (filteredTariffs: ITariff[]) => JSX.Element,
    initTariffs: ITariff[], // Или village или cities
    defaultValue?: ETariffTypes,
    menuItems: IMenuItem[],
    hasCityTariffs: boolean,
    hasVillageTariffs: boolean,
}

export const TariffsFilters = ({ isVillage, children, initTariffs,  defaultValue, menuItems, hasCityTariffs, hasVillageTariffs }: ITariffsFiltersProps): JSX.Element => {
    const { asPath } = useRouter();

    const [filters, setFilters] = useState(getInitialFilterItems(defaultValue, asPath));

    const toggleFilter = useCallback((id) => {
        setFilters(currFilters => {
            const currElem = currFilters.find(el => el.id === id);
            if (currElem) {
                const currentFilter = currElem.alternative && currFilters.find(el => el.alternative && el.checked);
                if (currentFilter && currentFilter.id !== currElem.id) {
                    currentFilter.checked = false;
                    sendGTMevent(`remove filter ${currentFilter.id}`, `${currentFilter.label}`);
                }

                const newState = !currElem.checked;

                currElem.checked = newState;

                sendGTMevent(`${newState ? 'add' : 'remove'} filter ${currElem.id}`, `${currElem.label}`);

                return [
                    ...currFilters,
                ];
            }
            return currFilters;
        });
    }, []);

    const filteredTariffs = useMemo(() => {
        let filterTariffs = initTariffs;

        filters.forEach((filter) => {
            if (filter.checked) {
                filterTariffs = filter.action(filterTariffs);
            }
        });
        const isNoneFilters = filters.every(item => !item.checked);
        if (isNoneFilters) {
            filterTariffs =  filterTariffs.filter(item => !(item.alias === 'constructor' && item.type === 'bundles'));
        }
        return filterTariffs;
    }, [filters, initTariffs]);

    return (
        <>
            <FiltersForTariffs
                activeItem={isVillage ? 1 : 0}
                hasCityTariffs={hasCityTariffs}
                hasVillageTariffs={hasVillageTariffs}
                tariffSwitcherData={menuItems}
                tariffFiltersData={filters}
                onToggleFilter={toggleFilter}
            />
            {children(filteredTariffs)}
        </>
    );
};
